import {ReactElement, ReactNode, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  useIsMounted,
  useModalControls,
  ModalButton,
  ResponsiveSheet,
} from '@chekinapp/ui';
import {ERR_INTERNET_DISCONNECTED_MESSAGE} from '@chekin/api';
import {getErrorMessage} from '@chekin/core';
import errorIcon from '../assets/icons/error.svg?no-inline';

export type ErrorModalProps = {
  onClose?: () => void;
  open?: boolean;
  message?: string;
  children?: ReactNode | ReactElement | string | null;
  iconSrc?: string;
  iconAlt?: string;
  title?: string | ReactNode;
  description?: string | ReactNode;
  iconProps?: {width: number; height: number};
};

type DisplayErrorDetails = {
  title?: string;
  message?: string | ReactElement;
  description?: string;
};
export function useErrorModal() {
  const {t} = useTranslation();
  const isMounted = useIsMounted();
  const {
    isOpen: isErrorModalOpen,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModalControls();

  const [errorTitle, setErrorTitle] = useState(t('service_temporarily_unavailable'));
  const [errorMessage, setErrorMessage] = useState<
    string | ReactElement | ReactElement[]
  >('');
  const [errorDescription, setErrorDescription] = useState<string | ReactElement>('');

  const displayError = useCallback(
    // TODO Any type. Should be fixed
    // eslint-disable-next-line
    (error: any = {}, {title, message, description}: DisplayErrorDetails = {}) => {
      const isConnectionIssue = error.name === ERR_INTERNET_DISCONNECTED_MESSAGE;
      if (!isMounted.current) {
        return;
      }

      const errorMessage = message || (isConnectionIssue ? '' : getErrorMessage(error));
      const errorTitle =
        title || error.status <= 500
          ? t(title ?? 'error')
          : isConnectionIssue
          ? t('no_internet_connection')
          : null;
      const errorDescription = isConnectionIssue
        ? t('no_internet_description')
        : description || '';

      if (errorMessage || isConnectionIssue) setErrorMessage(errorMessage);
      if (errorDescription) setErrorDescription(errorDescription);
      if (errorTitle) setErrorTitle(errorTitle);
      openErrorModal();
    },
    [isMounted, openErrorModal, t],
  );

  const ErrorModal = useCallback(
    ({
      open,
      onClose,
      title,
      message,
      description,
      children,
      iconSrc,
      iconAlt,
      iconProps,
    }: ErrorModalProps) => {
      const displayedTitle = title || errorTitle;
      const displayedMessage = message || errorMessage;
      const displayedDescription = description || errorDescription;

      const getTextContent = () => {
        if (displayedMessage) {
          return (
            <span className="flex flex-col gap-4">
              <span>{displayedMessage}</span>
              {displayedDescription && <span>{displayedDescription}</span>}
            </span>
          );
        }

        return (
          <span className="flex flex-col gap-4">
            <span>{t('we_found_error')}</span>
            <span>{t('try_again_or_contact')}</span>
          </span>
        );
      };

      return (
        <ResponsiveSheet
          open={open !== undefined ? open : isErrorModalOpen}
          onClose={closeErrorModal}
          title={displayedTitle}
          hideTitle
          closeOnOverlayClick={false}
          closeOnEscape={false}
          disableDrag
          className="error-modal"
          dialogClassName="error-modal__dialog max-w-[500px] sm:max-w-[500px]"
        >
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="modal__icon mx-auto flex justify-center">
              <img
                src={iconSrc || errorIcon}
                alt={iconAlt || 'Exclamation error mark'}
                {...(iconProps || {width: 150, height: 150})}
              />
            </div>
            <h2
              aria-hidden="true"
              className="error-modal__title m-0 text-[18px] font-semibold text-[var(--font-primary)]"
            >
              {displayedTitle}
            </h2>
            <div className="error-modal__text text-[16px] text-[var(--font-primary)]">
              {getTextContent()}
            </div>
            {children || (
              <ModalButton
                onClick={onClose || closeErrorModal}
                className="error-modal__button"
              >
                {t('ok')}
              </ModalButton>
            )}
          </div>
        </ResponsiveSheet>
      );
    },
    [isErrorModalOpen, errorDescription, closeErrorModal, errorMessage, errorTitle, t],
  );

  return {
    displayError,
    closeErrorModal,
    openErrorModal,
    ErrorModal,
  };
}
